<template>
  <div :class="`check-list check-list--${layout}`">
    <div
      v-for="option of data.options"
      :key="option.value"
      class="check-option"
      :class="{'clear': option.clearAll, 'checked': isChecked(option)}"
    >
      <label class="check-label" :for="option.value">
        <input
          :id="option.value"
          v-model="checkboxes"
          type="checkbox"
          :name="option.value"
          :value="option"
        >
        <span :style="checkboxBackground" class="checkmark" />
        {{ option.label }}
      </label>
      <v-popover placement="bottom-end" offset="8" :trigger="tooltipTrigger">
        <div
          v-if="option.tooltip"
          class="check-option__tooltip"
        >
          {{ tooltipCharacter }}
        </div>

        <template #popover>
          {{ option.tooltip }}
        </template>
      </v-popover>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'CheckList',

  props: {
    data: {
      type: Object,
      required: true
    },
    value: { // Answer
      type: Object,
      required: true
    },
    checkboxImage: {
      type: String,
      default: 'tick.svg'
    },
    tooltipCharacter: {
      type: String,
      default: '?'
    }
  },

  data () {
    return { checkboxes: [] }
  },

  computed: {
    ...mapGetters(['isMobile']),
    selectedValues () {
      return this.value && this.value.result
    },
    options () {
      return this.data.options
    },
    layout () {
      return this.data.layout
    },
    checkboxBackground () {
      const url = require(`@/assets/icons/${this.checkboxImage}`)
      return { '--checkbox-background': `url(${url})` }
    },
    tooltipTrigger () {
      let trigger = 'hover focus'
      if (this.isMobile) trigger += ' click'
      return trigger
    }
  },

  watch: {
    checkboxes (newVal, oldVal) {
      let newClear = newVal.find(option => option.clearAll)
      let oldClear = oldVal.find(option => option.clearAll)

      let result = newVal

      // Handle the 'clear all' button
      if (newClear) {
        if (!oldClear) {
          result = [newClear]
        } else if (result.length > 1) {
          result = newVal.filter(val => !val.clearAll)
        }
        this.$set(this, 'checkboxes', result)
      }

      this.handleCheck()
    }
  },

  mounted () {
    if (this.value && this.value.result) {
      this.$set(this, 'checkboxes', this.value.result)
    }
  },

  methods: {
    handleCheck () {
      let checkedValues = this.checkboxes.map(box => box.value)
      let selectedOptions = this.options.filter(option => checkedValues.includes(option.value))

      this.input({ result: selectedOptions })
    },

    isChecked (option) {
      return this.value.result && this.value.result.includes(option)
    },

    input (value) {
      this.$emit('input', value)
    }
  }
}
</script>

<style scoped lang="scss">
.check-list {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .check-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: $checklist-border-radius;
    background-color: $white;
    width: 100%;
    margin: $spacing-12 $spacing-0;
    line-height: 20px;
    font-size: 20px;
    box-shadow: $checklist-shadow;
    border: $checklist-border;
    position: relative;
    top: 0;
    color: $checklist-color;

    @media (max-width: $screen_md) {
      margin: 0 0 $spacing-16 0;
    }

    &.clear,
    &:last-of-type:nth-of-type(odd) {
      width: 100%;
    }

    &:hover {
      background-color: $checklist-hover-bg;
      border: $checklist-border-hover;
      box-shadow: $checklist-shadow-hover;
      top: -4px;
    }

    &.checked {
      border: $checklist-border-selected;
      box-shadow: $checklist-shadow-selected;
      background-color: $checklist-selected-bg;
      color: $checklist-selected-text;
      top: -4px;
    }

    .check-label {
      position: relative;
      margin: $spacing-0;
      width: 100%;
      padding: $spacing-16;
      padding-left: $spacing-24;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      text-align: left;
      cursor: pointer;

      @media (max-width: $screen_md) {
        padding: $spacing-12;
      }
    }

    input[type=checkbox] {
      display: none;
    }

    .checkmark {
      width: 24px;
      height: 24px;
      margin-right: $spacing-12;
      border: $checklist-checkbox-border;
      background-color: $checklist-checkbox-bg;
      border-radius: $checklist-checkbox-border-radius;

      @media (max-width: $screen_md) {
        width: 20px;
        height: 20px;
      }
    }

    &.checked {
      .checkmark {
        background-image: var(--checkbox-background);
        background-color: $checklist-checkbox-bg-selected;
        background-repeat: no-repeat;
        background-position: center;
        border: $checklist-checkbox-border-selected;
      }
    }

    input[type=checkbox]:checked ~ .checkmark {
      border: $checklist-checkbox-border-selected;
    }

    &__tooltip {
      text-align: center;
      line-height: $checklist-tooltip-icon-line-height;
      height: $checklist-tooltip-icon-height;
      width: $checklist-tooltip-icon-width;
      min-width: $checklist-tooltip-icon-width;
      margin: $spacing-0 $spacing-12;
      padding: $spacing-0;
      border: $checklist-tooltip-icon-border;
      border-radius: 50%;
      font-size: $checklist-tooltip-icon-font-size;
      font-weight: 700;
      cursor: help;
      color: $checklist-tooltip-icon-text;
      background-color: $checklist-tooltip-icon-bg;
      font-family: $checklist-tooltip-icon-font;
    }
  }

  @media (min-width: $screen-md + 1) {
    &--grid {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;

      .check-option {
        width: calc(50% - #{$spacing-12});
        margin-bottom: $spacing-12;

        &.clear {
          width: 100%;
        }
      }
    }
  }

}
</style>
